import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { toast } from "sonner";
import { Brain, Save, Plus, Trash2, Pencil, BookOpen } from "lucide-react";

function ScriptForm({ initial, onSave, onCancel }) {
  const [objection, setObjection] = useState(initial?.objection || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [script, setScript] = useState(initial?.script || "");
  const [why, setWhy] = useState(initial?.why_it_works || "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!objection.trim() || !script.trim()) {
      toast.error("Objection and script are required");
      return;
    }
    setBusy(true);
    try {
      await onSave({
        objection: objection.trim(),
        label: label.trim(),
        script: script.trim(),
        why_it_works: why.trim(),
        enabled,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Caller objection (what the customer says)
        </div>
        <Input
          value={objection}
          onChange={(e) => setObjection(e.target.value)}
          placeholder="e.g. I need to talk to my spouse first"
          className="btn-sharp font-mono"
          data-testid="script-objection-input"
        />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Label (short name for this technique)
        </div>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Three-Way Conference Close"
          className="btn-sharp font-mono"
          data-testid="script-label-input"
        />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Approved script (exact words the agent should say)
        </div>
        <Textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder='e.g. "Absolutely — let&apos;s get them on the line right now. What&apos;s the best number?"'
          className="btn-sharp font-mono min-h-[100px]"
          data-testid="script-text-input"
        />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Why it works (1 sentence, optional)
        </div>
        <Input
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="e.g. Removes the spouse stall by including them immediately"
          className="btn-sharp font-mono"
          data-testid="script-why-input"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="script-enabled-toggle" />
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-600">
            {enabled ? "Active (AI will use this)" : "Disabled"}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" className="btn-sharp text-xs uppercase tracking-widest" onClick={onCancel} data-testid="script-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="btn-sharp bg-black hover:bg-zinc-800 text-white text-xs uppercase tracking-widest font-bold"
            data-testid="script-save-button"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Save script"}
          </Button>
        </div>
      </div>
    </div>
  );
}


export default function CoachTraining() {
  const [rules, setRules] = useState("");
  const [savedRules, setSavedRules] = useState("");
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, sc] = await Promise.all([
        api.get("/admin/coach/settings"),
        api.get("/admin/coach/scripts"),
      ]);
      const r = s.data.custom_rules || "";
      setRules(r);
      setSavedRules(r);
      setScripts(sc.data.scripts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.put("/admin/coach/settings", { custom_rules: rules });
      setSavedRules(rules);
      toast.success("Brand rules updated. AI will use them on the next objection.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingRules(false);
    }
  };

  const addScript = async (payload) => {
    try {
      const { data } = await api.post("/admin/coach/scripts", payload);
      setScripts([data.script, ...scripts]);
      setAddOpen(false);
      toast.success("Script added — AI will prefer it for matching objections.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateScript = async (id, payload) => {
    try {
      const { data } = await api.put(`/admin/coach/scripts/${id}`, payload);
      setScripts(scripts.map((s) => (s.id === id ? data.script : s)));
      setEditing(null);
      toast.success("Script updated.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeScript = async (id) => {
    if (!window.confirm("Delete this script? The AI will stop using it immediately.")) return;
    try {
      await api.delete(`/admin/coach/scripts/${id}`);
      setScripts(scripts.filter((s) => s.id !== id));
      toast.success("Script deleted.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleEnabled = async (s) => {
    try {
      const { data } = await api.put(`/admin/coach/scripts/${s.id}`, {
        ...s, enabled: !s.enabled,
      });
      setScripts(scripts.map((x) => (x.id === s.id ? data.script : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const isDirty = rules !== savedRules;

  if (loading) {
    return <div className="p-8 text-center text-zinc-500" data-testid="coach-training-loading">Loading…</div>;
  }

  return (
    <div className="space-y-px bg-zinc-200 border border-zinc-200" data-testid="coach-training-container">
      {/* Brand rules */}
      <div className="bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-5 h-5 text-[#0055FF]" />
          <div>
            <div className="font-heading font-black tracking-tight text-xl">Brand voice & rules</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              The AI follows these rules on every objection lookup
            </div>
          </div>
        </div>
        <Textarea
          value={rules}
          onChange={(e) => setRules(e.target.value)}
          placeholder={`Examples:
- Always reference the caller's state.
- Never use the word "cheap" — say "affordable" instead.
- Use a warm Southern tone.
- Mention that approval is based on health today, which can change tomorrow.
- Always close with a soft-trial question.`}
          className="btn-sharp font-mono text-sm min-h-[160px]"
          data-testid="coach-rules-textarea"
          maxLength={8000}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {rules.length} / 8000 chars · {isDirty ? "Unsaved changes" : "All saved"}
          </div>
          <Button
            onClick={saveRules}
            disabled={savingRules || !isDirty}
            className="btn-sharp bg-black hover:bg-zinc-800 text-white text-xs uppercase tracking-widest font-bold disabled:opacity-50"
            data-testid="coach-rules-save-button"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {savingRules ? "Saving…" : "Save rules"}
          </Button>
        </div>
      </div>

      {/* Scripts library */}
      <div className="bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            <div>
              <div className="font-heading font-black tracking-tight text-xl">Approved script library</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                AI prefers these scripts as rebuttal #1 when a matching objection comes in
              </div>
            </div>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="btn-sharp bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs uppercase tracking-widest font-bold" data-testid="add-script-button">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add script
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading font-black text-2xl tracking-tight">Add approved script</DialogTitle>
              </DialogHeader>
              <ScriptForm
                onSave={addScript}
                onCancel={() => setAddOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Objection</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Label</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Script preview</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold">Active</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-bold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="scripts-table">
            {scripts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-zinc-500 py-8 text-sm">
                  No scripts yet. Add the rebuttals your top closers use so the AI can copy their voice.
                </TableCell>
              </TableRow>
            )}
            {scripts.map((s) => (
              <TableRow key={s.id} data-testid={`script-row-${s.id}`}>
                <TableCell className="font-mono text-sm max-w-[240px] truncate">{s.objection}</TableCell>
                <TableCell className="text-xs">{s.label || <span className="text-zinc-400">—</span>}</TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-[320px] truncate italic">"{s.script}"</TableCell>
                <TableCell>
                  <Switch
                    checked={s.enabled}
                    onCheckedChange={() => toggleEnabled(s)}
                    data-testid={`script-toggle-${s.id}`}
                  />
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Dialog open={editing?.id === s.id} onOpenChange={(open) => !open && setEditing(null)}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="btn-sharp text-xs uppercase tracking-widest" onClick={() => setEditing(s)} data-testid={`edit-script-${s.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    </DialogTrigger>
                    {editing?.id === s.id && (
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="font-heading font-black text-2xl tracking-tight">Edit script</DialogTitle>
                        </DialogHeader>
                        <ScriptForm
                          initial={editing}
                          onSave={(payload) => updateScript(editing.id, payload)}
                          onCancel={() => setEditing(null)}
                        />
                      </DialogContent>
                    )}
                  </Dialog>
                  <Button size="sm" variant="ghost" className="btn-sharp text-rose-600" onClick={() => removeScript(s.id)} data-testid={`delete-script-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
